<template>
  <section class="panel-controls" :aria-label="$t('communication-status-panel.controls')">
    <!--
      A grade das linhas mora aqui dentro, e não na <section>: a barra é o
      container que ela consulta, e a consulta só alcança os descendentes.
    -->
    <div class="panel-controls__layout">
      <!-- O que muda a forma de ver. -->
      <div class="panel-controls__group panel-controls__group--view">
        <div class="panel-controls__set" :title="$t('communication-status-panel.view')">
          <s-icon class="panel-controls__icon" :name="viewIcon" />
          <span class="panel-controls__label">{{ $t('communication-status-panel.view') }}</span>
          <div class="panel-controls__chips">
            <button
              v-for="option in viewOptions"
              :key="option.value"
              type="button"
              class="panel-controls__chip"
              :class="{ 'panel-controls__chip--on': option.value === view }"
              :aria-pressed="String(option.value === view)"
              @click="$emit('update:view', option.value)"
            >{{ option.label }}</button>
          </div>
        </div>

        <div class="panel-controls__set" :title="$t('communication-status-panel.group-by')">
          <s-icon class="panel-controls__icon" name="group-by" />
          <span class="panel-controls__label">{{ $t('communication-status-panel.group-by') }}</span>
          <div class="panel-controls__chips">
            <button
              v-for="option in groupingOptions"
              :key="option.value"
              type="button"
              class="panel-controls__chip"
              :class="{ 'panel-controls__chip--on': option.value === grouping }"
              :aria-pressed="String(option.value === grouping)"
              @click="$emit('update:grouping', option.value)"
            >{{ option.label }}</button>
          </div>
        </div>

        <div
          v-if="isGrid"
          class="panel-controls__set"
          :title="$t('communication-status-panel.chart')"
        >
          <s-icon class="panel-controls__icon" :name="chartIcon" />
          <span class="panel-controls__label">{{ $t('communication-status-panel.chart') }}</span>
          <div class="panel-controls__chips">
            <button
              v-for="option in chartOptions"
              :key="option.value"
              type="button"
              class="panel-controls__chip"
              :class="{ 'panel-controls__chip--on': option.value === chart }"
              :aria-pressed="String(option.value === chart)"
              @click="$emit('update:chart', option.value)"
            >{{ option.label }}</button>
          </div>
        </div>
      </div>

      <!-- O que recorta. -->
      <div class="panel-controls__group panel-controls__group--filter">
        <div class="panel-controls__set" :title="$t('communication-status-panel.filter-state')">
          <s-icon class="panel-controls__icon" name="filter" />
          <span class="panel-controls__label">
            {{ $t('communication-status-panel.filter-state') }}
          </span>
          <div class="panel-controls__chips">
            <button
              v-for="option in stateOptions"
              :key="option.value"
              type="button"
              class="panel-controls__chip"
              :class="[
                `panel-controls__chip--${option.value}`,
                { 'panel-controls__chip--on': isStateSelected(option.value) },
              ]"
              :aria-pressed="String(isStateSelected(option.value))"
              @click="toggleState(option.value)"
            >{{ option.label }}</button>
          </div>
          <button
            type="button"
            class="panel-controls__legend-toggle"
            :class="{ 'panel-controls__legend-toggle--on': legendOpen }"
            :aria-expanded="String(legendOpen)"
            :title="$t('communication-status-panel.legend-title')"
            :aria-label="$t('communication-status-panel.legend-title')"
            @click="legendOpen = !legendOpen"
          >
            <s-icon name="hint" />
          </button>
        </div>

        <!--
          A contagem do que casa e o "Limpar" moram no bloco da busca, colados ao
          controle que os produz: numa linha própria, cada filtro abriria uma
          linha nova na barra.
        -->
        <div class="panel-controls__set panel-controls__set--search">
          <s-icon class="panel-controls__icon" name="search" />
          <input
            :value="query"
            type="search"
            class="panel-controls__search"
            :placeholder="$t('communication-status-panel.search-placeholder')"
            :aria-label="$t('communication-status-panel.search-placeholder')"
            @input="$emit('update:query', $event.target.value)"
          >
          <span v-if="filterActive" class="panel-controls__matches">
            {{ $t('communication-status-panel.matches', { count: matchedCount }) }}
          </span>
          <button
            v-if="filterActive"
            type="button"
            class="panel-controls__clear"
            @click="clearFilter"
          >{{ $t('communication-status-panel.clear-filter') }}</button>
        </div>
      </div>
    </div>

    <state-rules-legend v-if="legendOpen" @close="legendOpen = false" />
  </section>
</template>

<script>
import { groupingLevels } from '@/domain/services/generalPanel/communicationStatusPanel/panelGrouping';
import { panelCharts } from '@/domain/enums/panelChart';
import { panelViews } from '@/domain/enums/panelView';
import { assetStatesBySeverity } from '@/domain/enums/assetState';
import StateRulesLegend from './StateRulesLegend';

export default {
  name: 'PanelControls',
  components: {
    StateRulesLegend,
  },
  props: {
    grouping: {
      type: String,
      required: true,
    },
    chart: {
      type: String,
      required: true,
    },
    view: {
      type: String,
      required: true,
    },
    states: {
      type: Array,
      default: () => [],
    },
    query: {
      type: String,
      default: '',
    },
    matchedCount: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({
    legendOpen: false,
  }),
  computed: {
    /**
     * Na Grade o agrupamento reúne cartões nos três níveis. No Radial ele define
     * o anel mais interno, e Ativo não é raiz possível: sobraria um anel só, que
     * é uma rosca.
     */
    groupingOptions() {
      const levels = this.isGrid
        ? [groupingLevels.COMPANY, groupingLevels.INSTALLATION, groupingLevels.ASSET]
        : [groupingLevels.COMPANY, groupingLevels.INSTALLATION];

      return levels.map(value => ({
        value,
        label: this.$t(`communication-status-panel.grouping.${value}`),
      }));
    },
    /**
     * A Visualização do Sensor só vale na Grade: no Radial o Sensor não vira
     * anel. Exibir um controle inerte ensinaria quem opera a duvidar dos que
     * funcionam.
     */
    isGrid() {
      return this.view === panelViews.GRID;
    },
    viewOptions() {
      return [panelViews.GRID, panelViews.RADIAL].map(value => ({
        value,
        label: this.$t(`communication-status-panel.views.${value}`),
      }));
    },
    viewIcon() {
      return this.isGrid ? 'view-grid' : 'view-radial';
    },
    chartOptions() {
      return [panelCharts.BAR, panelCharts.DONUT].map(value => ({
        value,
        label: this.$t(`communication-status-panel.charts.${value}`),
      }));
    },
    /** O ícone do grupo acompanha a visualização ativa. */
    chartIcon() {
      return this.chart === panelCharts.DONUT ? 'chart-donut' : 'chart-bar';
    },
    stateOptions() {
      return assetStatesBySeverity.map(value => ({
        value,
        label: this.$t(`communication-status-panel.state.${value}`),
      }));
    },
    filterActive() {
      return this.states.length > 0 || String(this.query || '').trim().length > 0;
    },
  },
  methods: {
    isStateSelected(state) {
      return this.states.indexOf(state) >= 0;
    },
    /** Alterna sem mutar o array recebido por prop. */
    toggleState(state) {
      const next = this.isStateSelected(state)
        ? this.states.filter(item => item !== state)
        : this.states.concat([state]);

      this.$emit('update:states', next);
    },
    clearFilter() {
      this.$emit('update:states', []);
      this.$emit('update:query', '');
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"

// Rótulo fora da tela, mas ainda lido pelo leitor de tela: sai da vista sem
// deixar o bloco sem nome.
=panel-controls-visually-hidden
  border: 0
  clip: rect(0 0 0 0)
  height: 1px
  margin: -1px
  overflow: hidden
  padding: 0
  position: absolute
  white-space: nowrap
  width: 1px

=panel-controls-visually-shown
  clip: auto
  height: auto
  margin: 0
  overflow: visible
  position: static
  width: auto

// Os limites medem o conteúdo da própria barra, não a janela: o menu lateral e
// o submenu tiram largura dela sem a janela mudar. Estimados sem filtro ativo;
// com filtro, a contagem e o "Limpar" (~13rem) cabem porque o campo de busca
// encolhe até 4rem para abrir espaço — o texto buscado costuma ser curto.
// Linha única sem os rótulos: cinco blocos só com ícone e opções (~68rem).
$single-line-min: 78rem
// Linha única com os rótulos em caixa alta e a busca na largura cheia (~91rem).
$labels-min: 100rem

// Ver Barra de Controles no CONTEXT.md: uma linha só enquanto couber, e quando
// não couber, duas linhas sempre na mesma divisão.
.panel-controls
  background: var(--panel-surface-raised)
  border-bottom: 1px solid var(--panel-border)
  container-name: panel-controls
  container-type: inline-size
  padding: 0.5rem 1.2rem
  position: relative

  // Padrão: duas linhas — em cima o que muda a forma de ver, embaixo o que
  // recorta. É também o que vê o navegador que não sabe medir a barra (TV com
  // navegador embarcado antigo). A linha única é ligada lá embaixo, por
  // @container.
  &__layout
    display: flex
    flex-direction: column
    gap: 0.5rem

  // Quebrar dentro do grupo é o último recurso, para uma janela estreita demais
  // até para as duas linhas: nunca empurra a página para fora da tela.
  &__group
    align-items: center
    display: flex
    flex-wrap: wrap
    gap: 0.5rem 0.75rem
    min-width: 0

  // Cada conjunto vira um bloco fechado, separado por moldura em vez de espaco
  // solto: com quatro grupos numa linha so, distancia sozinha nao agrupa.
  &__set
    align-items: center
    background: var(--panel-control-well)
    border: 1px solid var(--panel-border)
    border-radius: 6px
    display: flex
    gap: 0.4rem
    padding: 0.25rem 0.5rem

    // A busca é o único bloco que pode encolher: os outros são opções que
    // precisam aparecer inteiras.
    &--search
      margin-left: auto
      min-width: 0

  &__icon
    color: var(--panel-text-muted)
    font-size: 0.8rem

  // Os rótulos em caixa alta são os primeiros a sair quando a largura aperta:
  // ficam o ícone, com o nome do bloco como dica, e as opções, que se explicam
  // sozinhas ("Grade/Radial", "Barra/Rosca"). Só aparecem com espaço de sobra.
  &__label
    color: var(--panel-text-muted)
    font-size: 0.66rem
    letter-spacing: 0.07em
    text-transform: uppercase
    +panel-controls-visually-hidden

  &__chips
    display: flex
    gap: 0.25rem

  &__chip
    background: transparent
    border: 1px solid transparent
    border-radius: 999px
    color: var(--panel-text-muted)
    cursor: pointer
    font-size: 0.75rem
    line-height: 1
    padding: 0.3rem 0.7rem
    transition: background-color 160ms ease-out, color 160ms ease-out
    white-space: nowrap

    &:hover
      background: var(--panel-chip-hover)
      color: var(--panel-text)

    &--on
      background: var(--panel-match)
      color: var(--panel-background)
      font-weight: 700

    &--attention.panel-controls__chip--on
      background: var(--panel-attention)
      color: var(--panel-background)
    &--normal.panel-controls__chip--on
      background: var(--panel-normal)
      color: var(--panel-background)
    &--out-of-service.panel-controls__chip--on
      background: var(--panel-out-of-service)
      color: var(--panel-text)

  &__legend-toggle
    background: transparent
    border: none
    border-left: 1px solid var(--panel-border)
    color: var(--panel-text-muted)
    cursor: pointer
    font-size: 0.85rem
    line-height: 1
    margin-left: 0.15rem
    padding: 0.2rem 0 0.2rem 0.5rem

    &:hover,
    &--on
      color: var(--panel-match)

  // Nas duas linhas a busca tem a linha de baixo quase só para ela. Encolhe até
  // 4rem quando precisa dar lugar à contagem do filtro.
  &__search
    background: transparent
    border: none
    color: var(--panel-text)
    flex: 0 1 12rem
    font-size: 0.8rem
    min-width: 4rem
    padding: 0.25rem 0
    width: 12rem

    &::placeholder
      color: var(--panel-text-muted)

    &:focus
      outline: none

  &__matches
    border-left: 1px solid var(--panel-border)
    color: var(--panel-match)
    font-size: 0.74rem
    padding-left: 0.5rem
    white-space: nowrap

  &__clear
    background: transparent
    border: none
    color: var(--panel-match)
    cursor: pointer
    font-size: 0.72rem
    text-decoration: underline
    white-space: nowrap

  // Seletores `& &__x` (descendentes da raiz) de propósito: o CSS scoped do Vue
  // 2 só carimba `[data-v-…]` dentro de @media e @supports. Dentro de
  // @container a regra sairia sem o carimbo, menos específica que a regra base
  // carimbada, e perderia sempre. Com a raiz na frente a especificidade empata e
  // vale a ordem — estas vêm depois.

  // Linha única, sem os rótulos: os dois grupos lado a lado, a busca encostada
  // à direita e mais estreita.
  @container panel-controls (min-width: #{$single-line-min})
    & &__layout
      align-items: center
      flex-direction: row
      gap: 0.75rem

    & &__group
      flex-wrap: nowrap

    & &__group--filter
      flex: 1 1 auto

    & &__search
      flex-basis: 9rem
      width: 9rem

  // Com espaço de sobra, voltam os rótulos e a busca na largura cheia.
  @container panel-controls (min-width: #{$labels-min})
    & &__label
      +panel-controls-visually-shown

    & &__search
      flex-basis: 14rem
      width: 14rem

  // Celular: cada conjunto rola de lado se os chips não couberem, em vez de
  // empurrar a página para fora da tela. Chips mais altos para o dedo; a busca
  // toma a largura inteira. Aqui cada bloco tem linha própria, então o rótulo
  // cabe e volta.
  +media-max-sm()
    padding: 0.5rem 0.8rem

    &__label
      +panel-controls-visually-shown

    &__set
      max-width: 100%
      overflow-x: auto

      &--search
        flex: 1 1 100%
        margin-left: 0

    &__chip
      flex: none
      padding: 0.45rem 0.8rem

    &__search
      flex: 1
      min-width: 0
      width: auto
</style>
